import * as XLSX from 'xlsx'
import { jsPDF } from 'jspdf'

/**
 * Export data to CSV format
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions [{key, label}]
 * @param {string} filename - Name of the file to download
 */
export const exportToCSV = (data, columns, filename) => {
  try {
    // Create CSV header
    const headers = columns.map(col => col.label || col.key)
    const csvRows = [headers.join(',')]

    // Add data rows
    data.forEach(row => {
      const values = columns.map(col => {
        const value = row[col.key]
        // Handle null/undefined
        if (value === null || value === undefined) return ''
        // Handle arrays (e.g., products)
        if (Array.isArray(value)) return value.join('; ')
        // Handle objects
        if (typeof value === 'object') return JSON.stringify(value)
        // Escape commas and quotes in CSV
        const stringValue = String(value)
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`
        }
        return stringValue
      })
      csvRows.push(values.join(','))
    })

    // Create blob and download
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error exporting to CSV:', error)
    throw error
  }
}

/**
 * Export data to Excel format
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions [{key, label}]
 * @param {string} filename - Name of the file to download
 */
export const exportToExcel = (data, columns, filename) => {
  try {
    // Prepare worksheet data
    const worksheetData = []
    
    // Add headers
    const headers = columns.map(col => col.label || col.key)
    worksheetData.push(headers)

    // Add data rows
    data.forEach(row => {
      const values = columns.map(col => {
        const value = row[col.key]
        // Handle null/undefined
        if (value === null || value === undefined) return ''
        // Handle arrays
        if (Array.isArray(value)) return value.join('; ')
        // Handle objects
        if (typeof value === 'object') return JSON.stringify(value)
        return value
      })
      worksheetData.push(values)
    })

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(worksheetData)

    // Set column widths
    const colWidths = columns.map((col, idx) => {
      const maxLength = Math.max(
        (col.label || col.key).length,
        ...data.map(row => {
          const val = row[col.key]
          if (val === null || val === undefined) return 0
          if (Array.isArray(val)) return val.join('; ').length
          if (typeof val === 'object') return JSON.stringify(val).length
          return String(val).length
        })
      )
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) }
    })
    ws['!cols'] = colWidths

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

    // Generate file and download
    XLSX.writeFile(wb, `${filename}.xlsx`)
  } catch (error) {
    console.error('Error exporting to Excel:', error)
    throw error
  }
}

/**
 * Export data to PDF format
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions [{key, label}]
 * @param {string} title - Title for the PDF document
 * @param {string} filename - Name of the file to download
 */
export const exportToPDF = (data, columns, title, filename) => {
  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    })

    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 15
    let yPos = margin

    // Title
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(title, margin, yPos)
    yPos += 10

    // Date
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, margin, yPos)
    yPos += 8

    // Calculate column widths
    const numColumns = columns.length
    const tableWidth = pageWidth - (margin * 2)
    const colWidth = tableWidth / numColumns
    const startX = margin
    const headerHeight = 10
    const rowHeight = 8

    // Helper function to draw table header
    const drawHeader = (yPosition) => {
      // Header background
      doc.setFillColor(70, 130, 180) // Steel blue color
      doc.rect(startX, yPosition, tableWidth, headerHeight, 'F')
      
      // Header text
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(255, 255, 255) // White text
      
      columns.forEach((col, idx) => {
        const xPos = startX + (idx * colWidth)
        // Draw vertical line between columns (white separator)
        if (idx > 0) {
          doc.setDrawColor(255, 255, 255)
          doc.setLineWidth(0.2)
          doc.line(xPos, yPosition, xPos, yPosition + headerHeight)
        }
        // Center text in header cell
        doc.text(col.label || col.key, xPos + colWidth / 2, yPosition + headerHeight / 2 + 3, {
          maxWidth: colWidth - 4,
          align: 'center'
        })
      })
      
      // Draw outer borders
      doc.setDrawColor(50, 100, 150)
      doc.setLineWidth(0.3)
      doc.line(startX, yPosition, startX + tableWidth, yPosition) // Top
      doc.line(startX, yPosition, startX, yPosition + headerHeight) // Left
      doc.line(startX + tableWidth, yPosition, startX + tableWidth, yPosition + headerHeight) // Right
      doc.line(startX, yPosition + headerHeight, startX + tableWidth, yPosition + headerHeight) // Bottom
    }

    // Draw initial header
    drawHeader(yPos)
    yPos += headerHeight

    // Helper function to split text into lines that fit within cell width
    const splitTextIntoLines = (text, maxWidth) => {
      const lines = []
      const words = text.split(' ')
      let currentLine = ''
      
      words.forEach(word => {
        // Check if the word itself is too long (no spaces)
        const wordWidth = doc.getTextWidth(word)
        if (wordWidth > maxWidth) {
          // If current line has content, save it first
          if (currentLine) {
            lines.push(currentLine)
            currentLine = ''
          }
          // Split the long word character by character
          let wordPart = ''
          for (let i = 0; i < word.length; i++) {
            const testPart = wordPart + word[i]
            if (doc.getTextWidth(testPart) > maxWidth && wordPart) {
              lines.push(wordPart)
              wordPart = word[i]
            } else {
              wordPart = testPart
            }
          }
          if (wordPart) {
            currentLine = wordPart
          }
        } else {
          // Normal word wrapping
          const testLine = currentLine ? `${currentLine} ${word}` : word
          const textWidth = doc.getTextWidth(testLine)
          
          if (textWidth > maxWidth && currentLine) {
            lines.push(currentLine)
            currentLine = word
          } else {
            currentLine = testLine
          }
        }
      })
      
      if (currentLine) {
        lines.push(currentLine)
      }
      
      return lines.length > 0 ? lines : [text]
    }

    // Table rows
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(0, 0, 0)
    
    // Store initial table start position
    const tableStartY = yPos - headerHeight
    
    data.forEach((row, rowIdx) => {
      // First, calculate the maximum height needed for this row
      let maxLines = 1
      const cellLines = []
      
      columns.forEach((col, colIdx) => {
        // Get cell value
        let value = row[col.key]
        
        // Format value
        if (value === null || value === undefined) {
          value = 'N/A'
        } else if (Array.isArray(value)) {
          value = value.join(', ')
        } else if (typeof value === 'object') {
          value = JSON.stringify(value)
        } else {
          value = String(value)
        }
        
        // Split text into lines
        const availableWidth = colWidth - 4
        const lines = splitTextIntoLines(value, availableWidth)
        cellLines.push(lines)
        maxLines = Math.max(maxLines, lines.length)
      })
      
      // Calculate dynamic row height based on content
      const lineHeight = 5
      const padding = 2
      const dynamicRowHeight = (maxLines * lineHeight) + (padding * 2)
      
      // Check if we need a new page
      if (yPos + dynamicRowHeight > pageHeight - margin - 10) {
        doc.addPage()
        // On new pages, we don't need title/date, just header
        yPos = margin
        
        // Redraw header on new page
        drawHeader(yPos)
        yPos += headerHeight
      }

      // Row background (alternating)
      if (rowIdx % 2 === 0) {
        doc.setFillColor(255, 255, 255)
      } else {
        doc.setFillColor(245, 245, 250)
      }
      doc.rect(startX, yPos, tableWidth, dynamicRowHeight, 'F')

      // Cell borders and content
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.1)
      
      // Draw left border
      doc.line(startX, yPos, startX, yPos + dynamicRowHeight)
      
      columns.forEach((col, colIdx) => {
        const xPos = startX + (colIdx * colWidth)
        const lines = cellLines[colIdx]
        
        // Draw vertical line between columns
        if (colIdx > 0) {
          doc.line(xPos, yPos, xPos, yPos + dynamicRowHeight)
        }
        
        // Draw text lines
        lines.forEach((line, lineIdx) => {
          const lineY = yPos + padding + (lineIdx * lineHeight) + 3
          doc.text(line, xPos + colWidth / 2, lineY, {
            maxWidth: colWidth - 4,
            align: 'center'
          })
        })
      })

      // Draw right border
      doc.line(startX + tableWidth, yPos, startX + tableWidth, yPos + dynamicRowHeight)
      
      // Draw horizontal borders
      doc.line(startX, yPos, startX + tableWidth, yPos)
      doc.line(startX, yPos + dynamicRowHeight, startX + tableWidth, yPos + dynamicRowHeight)

      yPos += dynamicRowHeight
    })
    
    // Draw outer table border on first page
    doc.setPage(1)
    doc.setDrawColor(100, 100, 100)
    doc.setLineWidth(0.3)
    const firstPageEndY = Math.min(yPos, pageHeight - margin - 10)
    if (firstPageEndY > tableStartY) {
      // Top border
      doc.line(startX, tableStartY, startX + tableWidth, tableStartY)
      // Left border
      doc.line(startX, tableStartY, startX, firstPageEndY)
      // Right border
      doc.line(startX + tableWidth, tableStartY, startX + tableWidth, firstPageEndY)
      // Bottom border (if table ends on first page)
      if (yPos <= pageHeight - margin - 10) {
        doc.line(startX, firstPageEndY, startX + tableWidth, firstPageEndY)
      }
    }

    // Footer
    const totalPages = doc.internal.pages.length - 1
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      )
    }

    // Save PDF
    doc.save(`${filename}.pdf`)
  } catch (error) {
    console.error('Error exporting to PDF:', error)
    throw error
  }
}

